// === Shared HTML Generation ===

import { escapeHtml } from './dom.js';
import store from '../state/store.js';
import { MONTH_NAMES } from './date.js';

export function getDetailsHtml(item, resourcesHtml, taskList) {
  return `
    <span class="task-group-title">Key Actions</span>
    <ul class="task-list">
      ${taskList}
    </ul>

    ${item.content.notes ? `
    <div class="info-block">
        <span class="info-title">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Why it matters
        </span>
        <p class="info-text">${escapeHtml(item.content.notes)}</p>
    </div>` : ''}

    ${resourcesHtml}

    ${getCardToolsHtml(item)}
  `;
}

// Shared action row: add-to-calendar + share + move-month. Rendered inside
// the card detail modal via getDetailsHtml. Handlers live on window.app.
export function getCardToolsHtml(item) {
  const id = escapeHtml(item.id);
  return `
    <div class="card-tools">
      <button type="button" class="card-tool-btn" onclick="app.exportCard('${id}')" title="Add this task to your calendar">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4"/>
        </svg>
        <span>Add to calendar</span>
      </button>
      <button type="button" class="card-tool-btn" onclick="app.shareCard('${id}')" title="Share this task">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/>
        </svg>
        <span>Share</span>
      </button>
      ${getMoveMonthControl(item)}
    </div>
  `;
}

// Compact "move to another month" control styled like a card tool button.
// A transparent native <select> overlays the button so a tap opens the month
// picker; the label reflects the current override (or "Move month").
function getMoveMonthControl(item) {
  const id = escapeHtml(item.id);
  const cur = store.monthOverrides?.[item.id];
  const isActive = cur !== undefined && cur !== null;
  const label = isActive ? escapeHtml(MONTH_NAMES[cur]) : 'Move month';
  const opts = ['<option value="">Default timing</option>']
    .concat(MONTH_NAMES.map((n, i) => `<option value="${i}"${cur === i ? ' selected' : ''}>${escapeHtml(n)}</option>`))
    .join('');
  return `
    <label class="card-tool-btn card-tool-move${isActive ? ' is-active' : ''}" title="Move this task to another month that suits your farm">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="15" height="15" aria-hidden="true">
        <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
      </svg>
      <span>${label}</span>
      <select class="card-tool-move-select" aria-label="Move this task to another month"
              onchange="app.moveTaskMonth('${id}', this.value)">
        ${opts}
      </select>
    </label>
  `;
}

const TYPE_META = {
  pdf:        { badge: 'PDF',       cls: 'res-pdf' },
  video:      { badge: 'Video',     cls: 'res-video' },
  programme:  { badge: 'Programme', cls: 'res-programme' },
  bulletin:   { badge: 'Bulletin',  cls: 'res-bulletin' },
  publication:{ badge: 'Guide',     cls: 'res-publication' },
  teagasc:    { badge: 'Teagasc',   cls: 'res-teagasc' },
  weather:    { badge: 'Met Éireann', cls: 'res-weather' },
  training:   { badge: 'Training',  cls: 'res-training' },
  link:       { badge: 'Link',      cls: 'res-link' }
};

function detectType(url) {
  const u = (url || '').toLowerCase();
  if (u.includes('ibr_training')) return 'training';
  if (/\.pdf(\?|#|$)/.test(u)) return 'pdf';
  if (u.includes('youtube.com') || u.includes('youtu.be') || u.includes('vimeo.com')) return 'video';
  if (u.includes('animalhealthireland.ie/programmes/')) return 'programme';
  if (u.includes('animalhealthireland.ie/bulletins/')) return 'bulletin';
  if (u.includes('animalhealthireland.ie/publications/')) return 'publication';
  if (u.includes('teagasc.ie')) return 'teagasc';
  if (u.includes('met.ie')) return 'weather';
  return 'link';
}

function deriveLabel(url) {
  try {
    const u = new URL(url);
    const segs = u.pathname.split('/').filter(Boolean);
    const last = segs[segs.length - 1] || u.hostname.replace(/^www\./, '');
    let label = last
      .replace(/\.(pdf|html?|php|aspx?)$/i, '')
      .replace(/\?.*$/, '')
      .replace(/[-_]+/g, ' ')
      .replace(/\b(AHI|FINAL|Revised|Web Version|V\d+|\d{4})\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    label = label.split(' ')
      .filter(Boolean)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
    return label || 'Open resource';
  } catch {
    return 'Open resource';
  }
}

function normalize(input) {
  if (!input) return null;
  if (typeof input === 'object') {
    if (!input.url) return null;
    return {
      url: input.url,
      label: input.label || deriveLabel(input.url),
      herd: input.herd || null
    };
  }
  if (typeof input === 'string') {
    if (input.length < 10) return null;
    return { url: input, label: deriveLabel(input), herd: null };
  }
  return null;
}

function matchesCurrentHerd(resource) {
  if (!resource.herd) return true;
  const userHerd = (store.farmProfile?.herdType || '').toLowerCase();
  if (!userHerd) return true;
  const allowed = Array.isArray(resource.herd)
    ? resource.herd.map(h => h.toLowerCase())
    : [resource.herd.toLowerCase()];
  return allowed.includes(userHerd);
}

function iconSvg(type) {
  switch (type) {
    case 'pdf':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 14h6M9 18h4"/></svg>';
    case 'video':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polygon points="6 4 20 12 6 20 6 4" fill="currentColor"/></svg>';
    case 'programme':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>';
    case 'bulletin':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l18-8v18l-18-8z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></svg>';
    case 'publication':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>';
    case 'teagasc':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2L4 7l8 5 8-5-8-5z"/><path d="M4 12l8 5 8-5"/><path d="M4 17l8 5 8-5"/></svg>';
    case 'weather':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17.5 19a4.5 4.5 0 1 0 0-9 6 6 0 0 0-11.8 1.5A4 4 0 0 0 7 19z"/></svg>';
    case 'training':
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6"/><path d="M2 10l10-5 10 5-10 5-10-5z"/><path d="M6 12v5c0 1 4 3 6 3s6-2 6-3v-5"/></svg>';
    default:
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1"/><path d="M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/></svg>';
  }
}

export function prepareResources(item) {
  const mainLink = item.content?.primaryLink;
  const list = (item.content?.resources || [])
    .map(normalize)
    .filter(Boolean)
    .filter(matchesCurrentHerd);

  const items = [];
  if (mainLink) {
    const isTraining = mainLink.includes('ibr_training');
    items.push({
      url: mainLink,
      label: isTraining ? 'Open training module' : deriveLabel(mainLink),
      typeOverride: isTraining ? 'training' : null
    });
  }
  list.forEach(r => items.push(r));

  if (!items.length) return '';

  const linksHtml = items.map(r => {
    const type = r.typeOverride || detectType(r.url);
    const meta = TYPE_META[type] || TYPE_META.link;
    return `
      <a href="${escapeHtml(r.url)}" target="_blank" rel="noopener" class="resource-link ${meta.cls}">
        <span class="resource-icon" aria-hidden="true">${iconSvg(type)}</span>
        <span class="resource-label">${escapeHtml(r.label)}</span>
        <span class="resource-badge">${meta.badge}</span>
      </a>
    `;
  }).join('');

  return `
    <div class="info-block">
      <span class="info-title">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="14" height="14"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 015.656 0l1.414 1.414a4 4 0 010 5.656l-3.535 3.536a4 4 0 01-5.657 0L10.343 19.5M10.172 13.828a4 4 0 01-5.657 0l-1.414-1.414a4 4 0 010-5.657l3.535-3.535a4 4 0 015.657 0l1.364 1.364"/></svg>
        Resources
      </span>
      ${linksHtml}
    </div>
  `;
}
